import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import Avatar from "../components/Avatar";
import {
  User as UserIcon, Bell, KeyRound, Shield, Building2, ScrollText, Users,
  CreditCard, LifeBuoy, Sparkles, Plug, Fingerprint, ArrowRight, Search,
  BadgeCheck, Workflow as WorkflowIcon,
} from "lucide-react";

/*
 * Settings , HubSpot-style landing that fans out into dedicated sub-pages.
 * Every card links to a working route. No dead buttons.
 */

const ROLE_LABEL = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  hr: "HR Business Partner",
  manager: "People Manager",
  employee: "Employee",
};

function buildCards(user) {
  const isAdmin = user?.role === "company_admin";
  const isSuper = user?.role === "super_admin";
  const isHR = user?.role === "hr";
  const managePolicies = ["company_admin", "hr"].includes(user?.role);

  return [
    {
      group: "Your account",
      cards: [
        { icon: UserIcon,   title: "Profile",             desc: "Personal info, avatar, bio, contact details.",       to: "/profile" },
        { icon: KeyRound,   title: "Password & security", desc: "Change password, review sign-in activity.",          to: "/security" },
        { icon: Bell,       title: "Notifications",       desc: "Choose what pings you and how you receive it.",      to: "/settings/notifications" },
        { icon: Sparkles,   title: "Preferences",         desc: "Language, timezone, density, dashboards.",           to: "/settings/preferences" },
        { icon: Shield,     title: "Privacy & data",      desc: "Data-subject rights, exports, retention policy.",    to: "/settings/privacy" },
      ],
    },
    {
      group: "Workspace",
      when: () => isAdmin || isSuper,
      cards: [
        { icon: Building2,   title: "Workspace overview", desc: "Plan, seats, workspace-wide settings.",              to: "/admin",                    when: () => isAdmin },
        { icon: BadgeCheck,  title: "Company profile",    desc: "Legal name, address, contacts, branding.",           to: "/admin/company-profile",    when: () => isAdmin },
        { icon: Users,       title: "Users & roles",      desc: "Invite teammates, change roles, deactivate.",         to: "/admin/users",              when: () => isAdmin },
        { icon: WorkflowIcon, title: "Workflows",          desc: "Design and manage case-handling workflows.",         to: "/admin/workflows",          when: () => isAdmin },
        { icon: CreditCard,  title: "Billing & invoices", desc: "Plans, seats, and downloadable invoices.",           to: "/admin/billing",            when: () => isAdmin },
      ],
    },
    {
      group: "Content & governance",
      when: () => managePolicies,
      cards: [
        { icon: ScrollText,  title: "Policies",           desc: "Read and sign the policies published in your workspace.",   to: "/policies" },
        { icon: BookOpen,    title: "Resources",          desc: "Create, edit, and publish organization policies.",         to: isAdmin ? "/admin/resources" : "/hr/resources" },
      ],
    },
    {
      group: "Integrations & advanced",
      cards: [
        { icon: Plug,        title: "Integrations",       desc: "Slack, Teams, SSO, HRIS , connect PropHR.",         to: "/settings/integrations" },
        { icon: Fingerprint, title: "Audit log",          desc: "Every mutating action on your workspace.",           to: isAdmin ? "/admin/audit" : isHR ? "/hr/audit" : isSuper ? "/super/audit" : null, when: () => isAdmin || isHR || isSuper },
        { icon: LifeBuoy,    title: "Support desk",       desc: "Open a ticket, browse open conversations.",           to: "/tickets" },
      ],
    },
  ];
}

export default function Settings() {
  const { user, tenant, logout } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");

  const groups = useMemo(() => buildCards(user), [user]);

  const filteredGroups = useMemo(() => {
    if (!q.trim()) return groups;
    const needle = q.toLowerCase();
    return groups.map((g) => ({
      ...g,
      cards: (g.cards || []).filter((c) => c.title.toLowerCase().includes(needle) || c.desc.toLowerCase().includes(needle)),
    })).filter((g) => g.cards.length > 0);
  }, [groups, q]);

  const signOut = async () => {
    if (!window.confirm("Sign out of PropHR?")) return;
    logout();
    nav("/login");
  };

  return (
    <div className="pb-12" data-testid="settings-page">
      {/* Hero */}
      <div className="border-b border-slate-200 bg-gradient-to-br from-slate-50 via-white to-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
          <div className="flex items-start gap-5">
            <Avatar user={user} size={72}/>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono tracking-widest text-slate-500 uppercase mb-1">Settings · {tenant?.name || "PropHR"}</div>
              <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 leading-tight">
                {user?.name}
              </h1>
              <div className="text-sm text-slate-500 mt-1">
                {user?.email} · <span className="pill pill-closed">{ROLE_LABEL[user?.role] || user?.role}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 relative max-w-xl">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2}/>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search settings…"
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/10 shadow-sm"
              data-testid="settings-search"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-10 space-y-10">
        {filteredGroups.map((g) => {
          if (g.when && !g.when()) return null;
          const visibleCards = (g.cards || []).filter((c) => (c.when ? c.when() : true) && c.to);
          if (visibleCards.length === 0) return null;
          return (
            <section key={g.group} data-testid={`settings-group-${g.group.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="eyebrow mb-4">{g.group}</div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleCards.map((c) => (
                  <Link
                    key={c.title}
                    to={c.to}
                    className="group border border-slate-200 bg-white p-5 hover:border-slate-400 hover:shadow-sm transition-all flex flex-col"
                    data-testid={`setting-${c.title.toLowerCase().replaceAll(" ", "-")}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 border border-slate-200 bg-slate-50 group-hover:border-blue-600 group-hover:bg-blue-50 flex items-center justify-center transition-colors">
                        <c.icon size={16} strokeWidth={1.75} className="text-slate-700 group-hover:text-blue-700 transition-colors"/>
                      </div>
                      <ArrowRight size={14} strokeWidth={1.75} className="text-slate-300 group-hover:text-blue-700 group-hover:translate-x-0.5 transition-all"/>
                    </div>
                    <div className="font-display text-base font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">{c.title}</div>
                    <div className="text-xs text-slate-500 mt-1 leading-relaxed">{c.desc}</div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        {filteredGroups.length === 0 && (
          <div className="border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500 bg-slate-50/50">
            No settings match &ldquo;{q}&rdquo;.
          </div>
        )}

        {/* Sign-out at bottom */}
        <div className="pt-8 border-t border-slate-200">
          <button
            onClick={signOut}
            className="text-sm text-red-700 hover:text-red-900 underline"
            data-testid="settings-sign-out"
          >
            Sign out of this workspace
          </button>
        </div>
      </div>
    </div>
  );
}
